import { useState, useEffect } from "react";
import { ebayAPI } from "@/lib/api";
import { Spinner, PencilSimple } from "@phosphor-icons/react";
import EbayListingModal from "@/components/app/EbayListingModal";

export default function ListOnEbayButton({ verdict }) {
  const [status, setStatus] = useState("checking");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Check if already listed on eBay
  const alreadyListed = verdict.ebay_listing_id;

  useEffect(() => {
    if (alreadyListed) {
      setStatus("done");
      setResult({
        listing_id: verdict.ebay_listing_id,
        ebay_url: verdict.ebay_url || `https://www.ebay.com/itm/${verdict.ebay_listing_id}`,
      });
    } else {
      checkStatus();
    }
  }, []);

  const checkStatus = async () => {
    try {
      const { data } = await ebayAPI.authStatus();
      setStatus(data.connected && !data.expired ? "ready" : "disconnected");
    } catch {
      setStatus("disconnected");
    }
  };

  const handleConnect = async () => {
    try {
      const { data } = await ebayAPI.connect();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      }
    } catch {
      setError("Failed to start eBay connection");
    }
  };

  const handleSuccess = (data) => {
    setShowModal(false);
    setEditMode(false);
    if (!editMode) {
      setResult(data);
      setStatus("done");
    }
  };

  if (status === "checking") return null;

  if (status === "done") {
    return (
      <>
        <div className="p-3 bg-[#00E676] border-2 border-black" data-testid="ebay-listed">
          <div className="font-heading font-bold uppercase text-sm text-center">
            {result?.listing_id ? "Live on eBay!" : "Listed on eBay"}
          </div>
          <div className="flex items-center justify-center gap-3 mt-2">
            {result?.ebay_url && (
              <a
                href={result.ebay_url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 bg-white border-2 border-black font-heading text-xs font-bold uppercase hover:bg-neutral-100"
                data-testid="view-ebay-link"
              >
                View #{result.listing_id}
              </a>
            )}
            <button
              onClick={() => { setEditMode(true); setShowModal(true); }}
              className="px-3 py-1.5 bg-black text-white border-2 border-black font-heading text-xs font-bold uppercase flex items-center gap-1"
              data-testid="edit-listing-btn"
            >
              <PencilSimple size={12} weight="bold" /> Edit Listing
            </button>
          </div>
          {result?.publish_error && (
            <div className="font-body text-xs mt-2 text-left bg-white/50 p-2 border border-black break-all">
              <span className="font-semibold">eBay says: </span>{result.publish_error}
            </div>
          )}
        </div>
        {showModal && (
          <EbayListingModal
            verdict={{...verdict, photos: verdict.photos || []}}
            onClose={() => { setShowModal(false); setEditMode(false); }}
            onSuccess={handleSuccess}
            editMode
          />
        )}
      </>
    );
  }

  if (status === "disconnected") {
    return (
      <div className="space-y-2" data-testid="ebay-connect">
        <button
          onClick={handleConnect}
          className="w-full p-3 bg-[#002FA7] text-white font-heading font-bold uppercase tracking-wide text-sm border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:shadow-none transition-all"
          data-testid="ebay-connect-btn"
        >
          Connect eBay Account
        </button>
        {error && <div className="font-body text-xs text-red-600">{error}</div>}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2" data-testid="ebay-list">
        <button
          onClick={() => setShowModal(true)}
          className="w-full p-3 bg-[#002FA7] text-white font-heading font-bold uppercase tracking-wide text-sm border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:shadow-none transition-all flex items-center justify-center gap-2"
          data-testid="ebay-list-btn"
        >
          List on eBay
        </button>
        {error && <div className="font-body text-xs text-red-600 max-h-24 overflow-y-auto break-all bg-red-50 p-2 border border-red-200">{error}</div>}
      </div>
      {showModal && (
        <EbayListingModal
          verdict={verdict}
          onClose={() => setShowModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
}
